import { create } from 'zustand';

/**
 * Dispatches a run of the mounted canvas, resolving to the id of the run it
 * started, or null when the run was refused.
 *
 * The id is what lets the chat follow a run it did not start: the run card
 * reads a finished run's deliverables back by source run, so a dispatch that
 * kept its id to itself would leave the chat with nothing to show.
 */
export type CanvasRunHandler = () => Promise<string | null>;

export interface CanvasRunBridge {
  run: CanvasRunHandler;
  /**
   * Identity of the workflow the mounted canvas currently shows, same
   * scheme as `CanvasApplyMutationsBridge` -- never a bare null, or the
   * mismatch check below would let a delayed run dispatch start on
   * whatever canvas happens to be mounted by the time it fires.
   */
  workflowId: string | null;
}

interface CanvasRunBridgeState {
  /** The embedded canvas's dispatcher, or null when no canvas is mounted. */
  bridge: CanvasRunBridge | null;
  setBridge: (bridge: CanvasRunBridge | null) => void;
}

/**
 * Connects a chat rail to the workflow canvas mounted beside it, mirroring
 * `canvas-apply-mutations-bridge-store`.
 *
 * The agent's `run_workflow` tool only *asks* the frontend to start a run —
 * the canvas owns the dispatch, and with it validation, node statuses, and the
 * run timer. On the `/workflow/:id` route the chat lives inside the editor and
 * gets that dispatcher as a prop. In a surface host the chat is the editor's
 * sibling, so no prop path exists: the canvas publishes its dispatcher here
 * and the host's chat calls it.
 */
export const useCanvasRunBridgeStore = create<CanvasRunBridgeState>((set) => ({
  bridge: null,
  setBridge: (bridge) => set({ bridge }),
}));

/**
 * Run the mounted canvas, or resolve null when none is listening. Reads the
 * bridge at call time so the returned function is stable across renders.
 *
 * `targetWorkflowId`, when given, must match the mounted canvas's workflow --
 * without this, a run dispatch that started against one workflow (or one the
 * user has since navigated away from) could start on whatever canvas happens
 * to be mounted by the time it resolves.
 */
export async function dispatchCanvasRun(
  targetWorkflowId?: string | null,
): Promise<string | null> {
  const bridge = useCanvasRunBridgeStore.getState().bridge;
  if (!bridge) return null;
  if (
    targetWorkflowId != null &&
    bridge.workflowId != null &&
    targetWorkflowId !== bridge.workflowId
  ) {
    return null;
  }
  return bridge.run();
}
